'use client';

import { Dialog } from '@base-ui/react/dialog';
import { SidebarSimpleIcon, XIcon } from '@phosphor-icons/react';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { pushEvent } from '@/lib/faro';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Separator } from './separator';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_COLLAPSED = '3rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

type SidebarContextProps = {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  isMobile: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }
  return context;
}

function SidebarProvider({
  defaultOpen = true,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const [isCollapsed, setCollapsed] = React.useState(!defaultOpen);

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      setOpenMobile((open) => !open);
    } else {
      setCollapsed((collapsed) => {
        const newValue = !collapsed;
        // oxlint-disable-next-line unicorn/no-document-cookie -- simple cookie for sidebar state persistence
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${!newValue}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
        return newValue;
      });
    }
  }, [isMobile]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  // The mobile drawer is always full width, so it never renders the collapsed layout.
  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({ isCollapsed: isMobile ? false : isCollapsed, toggleSidebar, isMobile, openMobile, setOpenMobile }),
    [isCollapsed, toggleSidebar, isMobile, openMobile]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div className={cn('flex h-svh w-full overflow-hidden bg-mesa-cream', className)} {...props}>
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

function Sidebar({ className, children, ...props }: React.ComponentProps<'div'>) {
  const { isCollapsed, isMobile, openMobile, setOpenMobile, toggleSidebar } = useSidebar();

  if (isMobile) {
    return (
      <Dialog.Root open={openMobile} onOpenChange={setOpenMobile}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
          <Dialog.Popup
            className="fixed inset-y-0 left-0 z-40 flex h-full flex-col bg-mesa-card"
            style={{ width: SIDEBAR_WIDTH_MOBILE }}
          >
            <Dialog.Title className="sr-only">Navigation</Dialog.Title>
            {/* The drawer covers the header trigger that opened it, so it carries its own way out. */}
            <div className="flex justify-end p-2 pb-0">
              <Dialog.Close
                render={<Button variant="ghost" size="icon" className="size-7" />}
                aria-label="Close Sidebar"
              >
                <XIcon />
              </Dialog.Close>
            </div>
            {children}
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <div
      className={cn(
        'relative flex h-full shrink-0 flex-col border-r border-mesa-border bg-mesa-card transition-[width] duration-200 ease-linear',
        className
      )}
      style={{ width: isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH }}
      {...props}
    >
      <div className="flex h-full w-full flex-col overflow-hidden">{children}</div>
      <button
        onClick={toggleSidebar}
        className="absolute inset-y-0 -right-[6px] hidden w-3 cursor-pointer sm:flex after:absolute after:inset-y-0 after:left-1/2 after:w-[1px] after:-translate-x-1/2 after:bg-transparent hover:after:bg-mesa-border"
        aria-label="Toggle Sidebar"
      />
    </div>
  );
}

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  const { isCollapsed } = useSidebar();
  return <div className={cn('flex flex-col gap-2', isCollapsed ? 'p-1' : 'p-2', className)} {...props} />;
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-auto', className)} {...props} />;
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  const { isCollapsed } = useSidebar();
  return <div className={cn('flex flex-col gap-2', isCollapsed ? 'p-1' : 'p-2', className)} {...props} />;
}

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  const { isCollapsed } = useSidebar();
  return <div className={cn('flex w-full flex-col', isCollapsed ? 'p-1' : 'p-2', className)} {...props} />;
}

function SidebarGroupContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('w-full text-sm', className)} {...props} />;
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return <ul className={cn('flex w-full flex-col gap-1', className)} {...props} />;
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return <li className={cn('relative w-full', className)} {...props} />;
}

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  size = 'default',
  className,
  children,
  onClick,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  size?: 'default' | 'sm' | 'lg';
}) {
  const Comp = asChild ? Slot : 'button';
  const { isCollapsed } = useSidebar();

  return (
    <Comp
      className={cn(
        'flex w-full items-center gap-2 text-left text-sm outline-none ring-mesa-green transition-colors hover:bg-mesa-cream-dark hover:text-mesa-text focus-visible:ring-2',
        isActive && 'bg-mesa-cream-dark font-medium text-mesa-text',
        size === 'sm' && 'h-7 text-xs',
        size === 'lg' && 'h-12',
        isCollapsed ? 'justify-center p-2' : 'p-2',
        className
      )}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        const target = event.currentTarget;
        const label = target.textContent?.trim() || undefined;
        pushEvent('sidebar-menu-click', { label: label ?? '' });
        onClick?.(event);
      }}
      {...props}
    >
      {children}
    </Comp>
  );
}

function SidebarTrigger({ className, onClick, ...props }: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('size-7', className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <SidebarSimpleIcon />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

function SidebarSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
  return <Separator className={cn('mx-2 w-auto bg-mesa-border', className)} {...props} />;
}

function SidebarInset({ className, ...props }: React.ComponentProps<'main'>) {
  return (
    <main
      className={cn('relative flex h-full flex-1 flex-col overflow-y-auto bg-mesa-cream', className)}
      style={{ scrollbarGutter: 'stable' }}
      {...props}
    />
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
